// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useAppRewardConfiguration } from '@hooks/mutations/registry/onboarding/useAppRewardConfiguration';
import { useDialogControl } from '@hooks/other/useDialogControl';
import useHolderServices from '@hooks/queries/registry/onboarding/useHolderServices';
import { useSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, ButtonProps, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  HolderService,
  HolderServiceRequest,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Holder';
import {
  ProviderServiceRequest,
  ProviderService,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Provider';
import {
  RegistrarServiceRequest,
  RegistrarService,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Registrar';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { RejectDialog } from '../../../components/Dialog/RejectDialog';
import Loading from '../../../components/Loading';
import { NotificationBadge } from '../../../components/NotificationBadge';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useParties } from '../../../contexts/PartiesContext';
import useAcceptHolderServiceRequest from '../../../hooks/mutations/registry/onboarding/useAcceptHolderServiceRequest';
import useAcceptRegistrarServiceRequest from '../../../hooks/mutations/registry/onboarding/useAcceptRegistrarServiceRequest';
import useCancelHolderServiceRequest from '../../../hooks/mutations/registry/onboarding/useCancelHolderServiceRequest';
import useCancelProviderServiceRequest from '../../../hooks/mutations/registry/onboarding/useCancelProviderServiceRequest';
import useCancelRegistrarServiceRequest from '../../../hooks/mutations/registry/onboarding/useCancelRegistrarServiceRequest';
import useRejectHolderServiceRequest from '../../../hooks/mutations/registry/onboarding/useRejectHolderServiceRequest';
import useRejectProviderServiceRequest from '../../../hooks/mutations/registry/onboarding/useRejectProviderServiceRequest';
import useRejectRegistrarServiceRequest from '../../../hooks/mutations/registry/onboarding/useRejectRegistrarServiceRequest';
import useTerminateHolderService from '../../../hooks/mutations/registry/onboarding/useTerminateHolderService';
import useTerminateProviderService from '../../../hooks/mutations/registry/onboarding/useTerminateProviderService';
import useTerminateRegistrarService from '../../../hooks/mutations/registry/onboarding/useTerminateRegistrarService';
import useMutate from '../../../hooks/mutations/useMutate';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import useRegistryProviderConfiguration from '../../../hooks/queries/registry/configuration/useRegistryProviderConfiguration';
import useHolderServiceRequests from '../../../hooks/queries/registry/onboarding/useHolderServiceRequests';
import useProviderService from '../../../hooks/queries/registry/onboarding/useProviderService';
import useProviderServiceRequests from '../../../hooks/queries/registry/onboarding/useProviderServiceRequests';
import useProviderServices from '../../../hooks/queries/registry/onboarding/useProviderServices';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import useRegistrarServiceRequests from '../../../hooks/queries/registry/onboarding/useRegistrarServiceRequests';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import { validateFulfillsAllRequirements } from '../../../utils/credential';
import {
  ERR_ACC_HOLDER_SVC,
  ERR_ACC_REGISTRAR_SVC,
  ERR_CAN_HOLDER_SVC,
  ERR_CAN_PROVIDER_SVC,
  ERR_CAN_REGISTRAR_SVC,
  ERR_REJ_HOLDER_SVC,
  ERR_REJ_PROVIDER_SVC,
  ERR_REJ_REGISTRAR_SVC,
  ERR_TER_HOLDER_SVC,
  ERR_TER_PROVIDER_SVC,
  ERR_TER_REGISTRAR_SVC,
  SUC_ACC_HOLDER_SVC,
  SUC_ACC_REGISTRAR_SVC,
  SUC_CAN_HOLDER_SVC,
  SUC_CAN_PROVIDER_SVC,
  SUC_CAN_REGISTRAR_SVC,
  SUC_REJ_HOLDER_SVC,
  SUC_REJ_PROVIDER_SVC,
  SUC_REJ_REGISTRAR_SVC,
  SUC_TER_HOLDER_SVC,
  SUC_TER_PROVIDER_SVC,
  SUC_TER_REGISTRAR_SVC,
} from '../../../utils/strings';
import { PageWrapper } from '../../PageWrapper';
import { CreateAppRewardConfigurationDialog } from '../../settings/CreateAppRewardConfigurationDialog';
import { AddWeightDialog } from './AddWeightDialog';
import { RequestHolderServiceDialog } from './RequestHolderServiceDialog';
import { RequestProviderServiceDialog } from './RequestProviderServiceDialog';
import { RequestRegistrarServiceDialog } from './RequestRegistrarServiceDialog';
import { Row } from './types';

type Action = {
  name: string;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ variant, name, action, color }) => (
          <AsyncButton
            variant={variant}
            color={color}
            key={name}
            sx={{ mr: 1 }}
            onClick={() => action(params.row)}
          >
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'serviceType',
      headerName: 'Service Type',
      valueGetter: (params) => params.row.serviceType,
      flex: 150,
      filterable: true,
    },
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.operator} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.operator,
    },
    {
      field: 'provider',
      headerName: 'Provider',
      renderCell: (params) => <PartyDisplay party={params.row.provider} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.provider,
    },
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.registrar} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.registrar,
    },
    {
      field: 'user',
      headerName: 'User',
      renderCell: (params) => <PartyDisplay party={params.row.user} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.user,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

export const Onboarding = () => {
  const credentials = useCredentials();
  const appRewardConfigs = useAppRewardConfiguration();
  const appRewardConfigData = appRewardConfigs.data;
  const [value, setValue] = React.useState('0');
  const onChange = (_: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();

  const [onReject, setOnReject] = useState<((reason: string) => Promise<unknown>) | undefined>();
  const { party } = useParties();
  const holderServiceRequests = useHolderServiceRequests();
  const registrarService = useRegistrarService();
  const registrarServices = useRegistrarServices();
  const providerServices = useProviderServices();
  const holderServices = useHolderServices();
  const cancelHolderServiceRequest = useCancelHolderServiceRequest();
  const acceptHolderServiceRequest = useAcceptHolderServiceRequest();
  const registrarServiceRequests = useRegistrarServiceRequests();
  const providerServiceRequests = useProviderServiceRequests();
  const terminateRegistrarService = useTerminateRegistrarService();
  const terminateProviderService = useTerminateProviderService();
  const acceptRegistrarServiceRequest = useAcceptRegistrarServiceRequest();
  const rejectRegistrarServiceRequest = useRejectRegistrarServiceRequest();
  const rejectProviderServiceRequest = useRejectProviderServiceRequest();
  const rejectHolderServiceRequest = useRejectHolderServiceRequest();
  const cancelRegistrarServiceRequest = useCancelRegistrarServiceRequest();
  const cancelProviderServiceRequest = useCancelProviderServiceRequest();
  const providerConfiguration = useRegistryProviderConfiguration();
  const providerService = useProviderService();
  const terminateHolderService = useTerminateHolderService();
  const [selectedRow, setSelectedRow] = React.useState<Row | undefined>();
  const registrarServiceRequest = registrarServiceRequests.data?.find(
    (r) => r.payload.registrar === party,
  );
  const canRequestRegistrar = !registrarService.data && !registrarServiceRequest;
  const providerServiceRequest = providerServiceRequests.data?.find(
    (r) => r.payload.provider === party,
  );
  const mutate = useMutate();
  const holderService = holderServices.data?.find((r) => r.payload.holder === party);
  const holderServiceRequest = holderServiceRequests.data?.find((r) => r.payload.holder === party);
  const canRequestHolder = !holderService && !holderServiceRequest;

  const canRequestProvider = !providerService.data && !providerServiceRequest;
  const terminateHolder = useCallback(
    (r: Row) => {
      const payload = { holderServiceCid: r.contractId as ContractId<HolderService> };
      return mutate(terminateHolderService, payload, SUC_TER_HOLDER_SVC, ERR_TER_HOLDER_SVC);
    },
    [terminateHolderService, mutate],
  );
  const buttons = [
    {
      name: 'Request Holder Service',
      onClick: () => setDialogName('requestHolderService'),
      disabled: !canRequestHolder,
    },
    {
      disabled: !canRequestRegistrar,
      name: 'Request Registrar Service',
      onClick: () => setDialogName('registrar'),
    },
    {
      disabled: !canRequestProvider,
      name: 'Request Provider Service',
      onClick: () => setDialogName('provider'),
    },
  ];
  const { enqueueSnackbar } = useSnackbar();
  const { showBoundary } = useErrorBoundary<Error>();

  const terminateRegistrar = useCallback(
    (r: Row) => {
      const payload = { registrarServiceCid: r.contractId as ContractId<RegistrarService> };
      return mutate(
        terminateRegistrarService,
        payload,
        SUC_TER_REGISTRAR_SVC,
        ERR_TER_REGISTRAR_SVC,
      );
    },
    [terminateRegistrarService, mutate],
  );

  const terminateProvider = useCallback(
    (r: Row) => {
      const payload = { providerServiceCid: r.contractId as ContractId<ProviderService> };
      return mutate(terminateProviderService, payload, SUC_TER_PROVIDER_SVC, ERR_TER_PROVIDER_SVC);
    },
    [terminateProviderService, mutate],
  );

  const acceptRegistrar = useCallback(
    (r: Row) => {
      // Check if data is loaded
      if (!providerConfiguration.data) {
        showBoundary(
          new Error(ERR_ACC_REGISTRAR_SVC, { cause: `Provider configuration is not available.` }),
        );
        return Promise.resolve();
      }
      if (!credentials.data) {
        showBoundary(new Error(ERR_ACC_REGISTRAR_SVC, { cause: 'Credentials are not available.' }));
        return Promise.resolve();
      }
      // Find matching credentials
      const { registrarRequirements } = providerConfiguration.data.payload;
      const now = new Date(); // local time
      const utcNow = new Date(now.toUTCString()); // utc time
      const { fulfillsAllRequirements, fulfillingCredentials } = validateFulfillsAllRequirements(
        utcNow,
        r.user,
        registrarRequirements,
        credentials.data,
      );
      if (!fulfillsAllRequirements) {
        enqueueSnackbar(`${ERR_ACC_REGISTRAR_SVC}: Credentials could not be found.`, {
          variant: 'error',
        });
        return Promise.resolve();
      }
      // Accept the request
      const payload = {
        registrarServiceRequestCid: r.contractId as ContractId<RegistrarServiceRequest>,
        providerConfigurationCid: providerConfiguration.data.contractId,
        credentialCids: fulfillingCredentials.map((c) => c.contractId),
      };
      return mutate(
        acceptRegistrarServiceRequest,
        payload,
        SUC_ACC_REGISTRAR_SVC,
        ERR_ACC_REGISTRAR_SVC,
      );
    },
    [
      acceptRegistrarServiceRequest,
      credentials,
      providerConfiguration,
      mutate,
      showBoundary,
      enqueueSnackbar,
    ],
  );

  const rejectRegistrar = useCallback(
    (r: Row, rejectReason: string) => {
      const payload = {
        registrarServiceRequestCid: r.contractId as ContractId<RegistrarServiceRequest>,
        rejectReason,
      };
      return mutate(
        rejectRegistrarServiceRequest,
        payload,
        SUC_REJ_REGISTRAR_SVC,
        ERR_REJ_REGISTRAR_SVC,
      );
    },
    [rejectRegistrarServiceRequest, mutate],
  );

  const rejectProvider = useCallback(
    (r: Row, rejectReason: string) => {
      const payload = {
        providerServiceRequestCid: r.contractId as ContractId<ProviderServiceRequest>,
        rejectReason,
      };
      return mutate(
        rejectProviderServiceRequest,
        payload,
        SUC_REJ_PROVIDER_SVC,
        ERR_REJ_PROVIDER_SVC,
      );
    },
    [rejectProviderServiceRequest, mutate],
  );
  const openModifyDialog = useCallback(
    async (r: Row) => {
      setDialogName('modify');
      setSelectedRow(r);
      return Promise.resolve();
    },
    [setDialogName, setSelectedRow],
  );
  const acceptHolder = useCallback(
    (r: Row) => {
      // Check if data is loaded
      if (!providerConfiguration.data) {
        showBoundary(
          new Error(ERR_ACC_HOLDER_SVC, { cause: `Provider configuration is not available.` }),
        );
        return Promise.resolve();
      }
      if (!credentials.data) {
        showBoundary(new Error(ERR_ACC_HOLDER_SVC, { cause: 'Credentials are not available.' }));
        return Promise.resolve();
      }
      // Find matching credentials
      const { holderRequirements } = providerConfiguration.data.payload;
      const now = new Date(); // local time
      const utcNow = new Date(now.toUTCString()); // utc time
      const { fulfillsAllRequirements, fulfillingCredentials } = validateFulfillsAllRequirements(
        utcNow,
        r.user,
        holderRequirements,
        credentials.data,
      );
      if (!fulfillsAllRequirements) {
        enqueueSnackbar(`${ERR_ACC_HOLDER_SVC}: Credentials could not be found.`, {
          variant: 'error',
        });
        return Promise.resolve();
      }

      // Accept the request
      const payload = {
        holderServiceRequestCid: r.contractId as ContractId<HolderServiceRequest>,
        providerConfigurationCid: providerConfiguration.data.contractId,
        credentialCids: fulfillingCredentials.map((c) => c.contractId),
      };
      return mutate(acceptHolderServiceRequest, payload, SUC_ACC_HOLDER_SVC, ERR_ACC_HOLDER_SVC);
    },
    [
      acceptHolderServiceRequest,
      credentials,
      providerConfiguration,
      mutate,
      showBoundary,
      enqueueSnackbar,
    ],
  );
  const rejectHolder = useCallback(
    (r: Row, rejectReason: string) => {
      const payload = {
        holderServiceRequestCid: r.contractId as ContractId<HolderServiceRequest>,
        rejectReason,
      };
      return mutate(rejectHolderServiceRequest, payload, SUC_REJ_HOLDER_SVC, ERR_REJ_HOLDER_SVC);
    },
    [rejectHolderServiceRequest, mutate],
  );
  const cancelHolder = useCallback(
    (r: Row) => {
      const payload = { holderServiceRequestCid: r.contractId as ContractId<HolderServiceRequest> };
      return mutate(cancelHolderServiceRequest, payload, SUC_CAN_HOLDER_SVC, ERR_CAN_HOLDER_SVC);
    },
    [cancelHolderServiceRequest, mutate],
  );
  const cancelRegistrar = useCallback(
    (r: Row) => {
      const payload = {
        registrarServiceRequestCid: r.contractId as ContractId<RegistrarServiceRequest>,
      };
      return mutate(
        cancelRegistrarServiceRequest,
        payload,
        SUC_CAN_REGISTRAR_SVC,
        ERR_CAN_REGISTRAR_SVC,
      );
    },
    [cancelRegistrarServiceRequest, mutate],
  );

  const cancelProvider = useCallback(
    (r: Row) => {
      const payload = {
        providerServiceRequestCid: r.contractId as ContractId<ProviderServiceRequest>,
      };
      return mutate(
        cancelProviderServiceRequest,
        payload,
        SUC_CAN_PROVIDER_SVC,
        ERR_CAN_PROVIDER_SVC,
      );
    },
    [cancelProviderServiceRequest, mutate],
  );
  const [provider, setProvider] = React.useState('');
  const onCloseCreateAppReward = () => {
    setProvider('');
    onCloseDialog();
  };
  const createServiceActions = useCallback(
    (r: Row) => {
      const actions: Action[] = [];
      if (r.serviceType === 'HolderService') {
        if (r.provider === party)
          actions.push({
            color: 'error',
            name: 'Terminate',
            action: terminateHolder,
          });
      }
      if (r.serviceType === 'RegistrarService' && r.provider === party)
        actions.push({ color: 'error', name: 'Terminate', action: terminateRegistrar });
      if (r.serviceType === 'ProviderService' && r.operator === party) {
        actions.push({ color: 'error', name: 'Terminate', action: terminateProvider });

        if (!r.appRewardConfig) {
          actions.push({
            name: 'Create App Reward',
            action: () => {
              setDialogName('create');
              setProvider(r.provider);
              return Promise.resolve();
            },
          });
        }
      }
      return actions;
    },
    [party, terminateProvider, terminateRegistrar, terminateHolder, setDialogName],
  );

  const createRequestActions = (r: Row) => {
    const actions: Action[] = [];
    if (r.operator === party && r.serviceType === 'ProviderService') {
      actions.push(
        { name: 'Accept', action: () => openModifyDialog(r) },
        {
          name: 'Reject',
          action: () => {
            setOnReject(() => (reason: string) => rejectProvider(r, reason));
            return Promise.resolve();
          },
        },
      );
    }
    if (r.provider === party) {
      if (r.serviceType === 'HolderService')
        actions.push(
          { name: 'Accept', action: acceptHolder },
          {
            name: 'Reject',
            action: () => {
              setOnReject(() => (reason: string) => rejectHolder(r, reason));
              return Promise.resolve();
            },
          },
        );
      if (r.serviceType === 'RegistrarService')
        actions.push(
          { name: 'Accept', action: acceptRegistrar },
          {
            name: 'Reject',
            action: () => {
              setOnReject(() => (reason: string) => rejectRegistrar(r, reason));
              return Promise.resolve();
            },
          },
        );
    }
    if (r.user === party) {
      if (r.serviceType === 'ProviderService')
        actions.push({ name: 'Cancel', action: cancelProvider });
      if (r.serviceType === 'RegistrarService')
        actions.push({ name: 'Cancel', action: cancelRegistrar });
      if (r.serviceType === 'HolderService') actions.push({ name: 'Cancel', action: cancelHolder });
    }
    return actions;
  };
  const isLoading =
    holderServiceRequests.isLoading ||
    !holderServiceRequests.data ||
    holderServices.isLoading ||
    credentials.isLoading ||
    registrarService.isLoading ||
    registrarServices.isLoading ||
    providerServices.isLoading ||
    registrarServiceRequests.isLoading ||
    providerServiceRequests.isLoading ||
    appRewardConfigs.isLoading ||
    !holderServices.data ||
    !credentials.data ||
    !registrarServices.data ||
    !providerServices.data ||
    !registrarServiceRequests.data ||
    !providerServiceRequests.data;
  if (isLoading) return <Loading />;

  const serviceRows = holderServices.data
    .map((r) => ({
      contractId: r.contractId as string,
      serviceType: 'HolderService',
      operator: r.payload.operator,
      provider: r.payload.provider,
      registrar: '',
      user: r.payload.holder,
    }))
    .concat(
      registrarServices.data.map((r) => ({
        contractId: r.contractId as string,
        serviceType: 'RegistrarService',
        operator: r.payload.operator,
        provider: r.payload.provider,
        registrar: r.payload.registrar,
        user: r.payload.registrar,
      })),
    )
    .concat(
      providerServices.data.map((r) => ({
        contractId: r.contractId as string,
        serviceType: 'ProviderService',
        operator: r.payload.operator,
        provider: r.payload.provider,
        registrar: '',
        user: r.payload.provider,
        appRewardConfig: appRewardConfigData?.find(
          (config) =>
            config.payload.operator === r.payload.operator &&
            config.payload.provider === r.payload.provider,
        ),
      })),
    );
  const requestRows = holderServiceRequests.data
    .map((r) => ({
      contractId: r.contractId as string,
      serviceType: 'HolderService',
      operator: r.payload.operator,
      provider: r.payload.provider,
      registrar: '',
      user: r.payload.holder,
    }))
    .concat(
      registrarServiceRequests.data.map((r) => ({
        contractId: r.contractId as string,
        serviceType: 'RegistrarService',
        operator: r.payload.operator,
        provider: r.payload.provider,
        registrar: r.payload.registrar,
        user: r.payload.registrar,
      })),
    )
    .concat(
      providerServiceRequests.data.map((r) => ({
        contractId: r.contractId as string,
        serviceType: 'ProviderService',
        operator: r.payload.operator,
        provider: r.payload.provider,
        registrar: '',
        user: r.payload.provider,
      })),
    );

  return (
    <PageWrapper buttons={buttons} pageName="Onboarding">
      {!!onReject && (
        <RejectDialog
          open={!!onReject}
          onClose={() => setOnReject(undefined)}
          onReject={onReject}
        />
      )}

      <Tabs value={value} onChange={onChange}>
        <Tab sx={{ textTransform: 'capitalize' }} value="0" label="services" />
        <Tab
          value="1"
          label={
            <Box
              sx={{
                textTransform: 'capitalize',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Typography variant="body2" sx={{ mr: 1 }}>
                Requests
              </Typography>
              <NotificationBadge value={requestRows.length || 0} />
            </Box>
          }
        />
      </Tabs>

      {dialogName === 'modify' && selectedRow && (
        <AddWeightDialog row={selectedRow} onClose={onCloseDialog} open={!!selectedRow} />
      )}
      {dialogName === 'requestHolderService' && (
        <RequestHolderServiceDialog open onClose={onCloseDialog} />
      )}
      {dialogName === 'provider' && <RequestProviderServiceDialog open onClose={onCloseDialog} />}
      {dialogName === 'registrar' && <RequestRegistrarServiceDialog open onClose={onCloseDialog} />}

      {value === '0' && (
        <DataTable
          columns={createHeaders(createServiceActions)}
          rows={serviceRows}
          rowKey={(a) => a.contractId}
        />
      )}
      {value === '1' && (
        <DataTable
          columns={createHeaders(createRequestActions)}
          rows={requestRows}
          rowKey={(a) => a.contractId}
        />
      )}
      {dialogName === 'create' && (
        <CreateAppRewardConfigurationDialog
          provider={provider}
          open
          onClose={onCloseCreateAppReward}
        />
      )}
    </PageWrapper>
  );
};
